import React from 'react';
import numeral from 'numeral';
import moment from 'moment';
import { convertToCurrency } from '@joinforma/ui/helpers/currency';

import { TRANSACTION } from 'constants/status';
import { WalletTransactionDateOption } from 'constants/transactions';
import date_helper from 'utils/date-helper';

import Currency from '../components/common/Currency';
import { getAmountToPointRatio } from './company';

export const formatAmountOrPoints = ({
  company,
  value,
  currency,
}: {
  company: {
    features: {
      stipend_config: {
        display_as_amount: boolean;
      };
    };
  };
  value: number;
  currency?: string;
}) => {
  const { display_as_amount } = company.features.stipend_config;

  return display_as_amount ? (
    <Currency amount={value || 0} currency={currency} />
  ) : (
    <span>{value} pts</span>
  );
};

export const showAmountOrPoints = ({
  company,
  value,
  currency,
}: {
  company: {
    features: {
      stipend_config: {
        display_as_amount: boolean;
        amount_to_points_ratio?: number;
      };
    };
  };
  value: number;
  currency?: string;
}) => {
  const { display_as_amount, amount_to_points_ratio = 1 } =
    company.features.stipend_config;
  const points = numeral(value).multiply(amount_to_points_ratio).value();
  return display_as_amount
    ? convertToCurrency({ amount: value, currency })
    : `${points} pts`;
};

// This function is deprecated. This function returns JSX elements, which is not
// what a utils file should do. Use showAmonutOrPoints instead
export const getAmountToPoints = ({
  company,
  total,
  format = true,
  round = false,
  currency,
}: {
  company: {
    features: {
      stipend_config: {
        display_as_amount: boolean;
        amount_to_points_ratio?: number;
      };
    };
  };
  currency?: string;
  format?: boolean;
  round?: boolean;
  total: number | { value: number };
}) => {
  const amount_to_points_ratio = getAmountToPointRatio(company);
  const value = numeral(total || 0)
    .multiply(amount_to_points_ratio)
    .value();
  const displayValue = round ? Math.round(value) : value;

  if (format) {
    return formatAmountOrPoints({ company, currency, value: displayValue });
  }

  return displayValue;
};

export const getPointsToAmount = ({
  company,
  points,
  format = true,
  currency,
}: {
  company: {
    features: {
      stipend_config: {
        display_as_amount: boolean;
        amount_to_points_ratio?: number;
      };
    };
  };
  points: number;
  format?: boolean;
  currency?: string;
}) => {
  const amount_to_points_ratio = getAmountToPointRatio(company);

  const amount = numeral(points || 0)
    .divide(amount_to_points_ratio)
    .value();

  if (format) {
    return formatAmountOrPoints({ company, value: amount, currency });
  }
  return amount;
};

export const getStatusColor = (status: string) => {
  let status_color = 'success';

  if (['pending', 'pending_cancel', 'processing'].indexOf(status) !== -1) {
    status_color = 'warning';
  }

  if (['rejected', 'cancelled', 'company_cancelled'].indexOf(status) !== -1) {
    status_color = 'error';
  }

  return status_color;
};

export const getReimbursementPeriodMonths = (
  period_start: string | number | Date,
  period_end: string | number | Date
) =>
  moment(period_end)
    .endOf('month')
    .diff(moment(period_start).startOf('month'), 'months') + 1;

export const getEmployeeTransactionCurrency = ({
  amount,
  employee,
  currency_info,
}: {
  amount: number;
  employee?: {
    settings?: { currency: string };
  };
  currency_info?: { currency?: string };
}) => {
  const { currency = 'USD' } = (employee && employee.settings) || {};
  return convertToCurrency({
    amount,
    currency: (currency_info && currency_info.currency) || currency,
  });
};

export const getOrderStatus = (status: string) => {
  switch (status) {
    case TRANSACTION.CONFIRMED:
    case TRANSACTION.PENDING:
    case TRANSACTION.FULFILLED:
      return TRANSACTION.CONFIRMED;
    case TRANSACTION.REFUNDED:
      return TRANSACTION.CANCELLED;
    default:
      return TRANSACTION.CONFIRMED;
  }
};

export const getDateRangeFromAccountTransactionDateOptions = (
  dateOption: WalletTransactionDateOption
): [string, string] => {
  const today = date_helper().format('YYYY-MM-DD');
  switch (dateOption) {
    case WalletTransactionDateOption.LAST_90_DAYS:
      return [date_helper().subtract(90, 'days').format('YYYY-MM-DD'), today];
    case WalletTransactionDateOption.CURRENT_YEAR:
      return [date_helper().startOf('year').format('YYYY-MM-DD'), today];
    case WalletTransactionDateOption.LAST_YEAR:
      return [
        date_helper().subtract(1, 'year').startOf('year').format('YYYY-MM-DD'),
        date_helper().subtract(1, 'year').endOf('year').format('YYYY-MM-DD'),
      ];
    case WalletTransactionDateOption.LAST_60_DAYS:
    default:
      return [date_helper().subtract(60, 'days').format('YYYY-MM-DD'), today];
  }
};
